import React, { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import Select from "react-select";

import Label from "@components/form/Label";
import Card from "@components/layout/Card";
import Layout from "@components/layout/Layout";
import Loading from "@components/shared/Loading";
import { getBillingReports } from "@services/reports";
import { useAllAccountingPeriods } from "@hooks/query/useAllAccountingPeriods";
import SelectLevel1Resource from "@components/form/SelectLevel1Resource";
import {
  BillingPeriodKey,
  getBillingReportDateRangeOptions,
} from "@utils/billingReportDateRanges";

const BillingReports: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [renderers, setRenderers] = useState<any>({});
  const [SelectedReport, setSelectedReport] = useState<any>();
  const [reportProps, setReportProps] = useState<{
    report?: any;
    level1ResourceId?: string;
    accountingPeriodId?: string;
    periodKey?: BillingPeriodKey;
    fixedFromDate?: string;
    fixedToDate?: string;
    varFromDate?: string;
    varToDate?: string;
    runAt?: Date;
  }>({});

  const [filter, setFilter] = useState<{
    report?: any;
    level1ResourceId?: string;
    accountingPeriodId?: string;
    periodKey?: BillingPeriodKey;
    fixedFromDate?: string;
    fixedToDate?: string;
    varFromDate?: string;
    varToDate?: string;
  }>({});

  const { data: accountingPeriods = [] } = useAllAccountingPeriods({
    params: {
      level1ResourceId: filter.level1ResourceId,
    },
    options: {
      enabled: !!filter.level1ResourceId,
      refetchOnWindowFocus: false,
    },
  });

  useQuery(["reports", "billing"], getBillingReports, {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    onSuccess: data => {
      const updatedRenderers = { ...renderers };
      for (const report of data) {
        updatedRenderers[report.id] = lazy(
          () => import(`@components/reports/${report.type}`),
        );
      }

      setRenderers(updatedRenderers);
      setSelectedReport(undefined);

      const selectedReport = data.find((r: any) => r.id === id) || data[0];
      if (selectedReport) {
        setFilter({ ...filter, report: selectedReport });
      }
    },
  });

  const getDateRangeOptions = () =>
    getBillingReportDateRangeOptions(accountingPeriods);

  const handleFilterChange = (field: string, value: any) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleDateRangeChange = (
    accountingPeriodId: string,
    periodKey: BillingPeriodKey,
    fixedFromDate: string,
    fixedToDate: string,
    varFromDate: string,
    varToDate: string,
  ) => {
    setFilter({
      ...filter,
      accountingPeriodId,
      periodKey,
      fixedFromDate,
      fixedToDate,
      varFromDate,
      varToDate,
    });
  };

  const handleRenderReport = (e: any) => {
    e.preventDefault();

    if (renderers[filter.report?.id]) {
      setSelectedReport(renderers[filter.report?.id]);
      setReportProps({
        report: filter.report,
        level1ResourceId: filter.level1ResourceId,
        accountingPeriodId: filter.accountingPeriodId,
        periodKey: filter.periodKey,
        fixedFromDate: filter.fixedFromDate,
        fixedToDate: filter.fixedToDate,
        varFromDate: filter.varFromDate,
        varToDate: filter.varToDate,
        runAt: new Date(),
      });
    }
  };

  return (
    <Layout
      permissions={["ViewReports"]}
      breadcrumb={[
        {
          label: t("dashboard.dashboard"),
          href: "/polestar",
        },
        {
          label: t("reporting.title"),
        },
      ]}
      title={t("reporting.new_billing_report")}
    >
      <Card className="flex flex-col rounded-none p-0 grow">
        <div className="flex flex-col grow gap-4 divide-y">
          <form
            className="flex items-end gap-2 p-4"
            onSubmit={handleRenderReport}
          >
            <div className="w-1/3">
              <Label htmlFor="report">{t("reporting.filter_level1wrs")}</Label>
              <SelectLevel1Resource
                className="w-full"
                value={filter.level1ResourceId}
                onChange={(e: any) => {
                  handleFilterChange("level1ResourceId", e.value);
                }}
              />
            </div>
            <div className="w-1/3">
              <Label htmlFor="report">{t("reporting.filter_dates")}</Label>
              <Select
                options={getDateRangeOptions()}
                className="w-full"
                value={getDateRangeOptions()?.find(
                  (e: any) =>
                    e.value.accountingPeriodId === filter.accountingPeriodId &&
                    e.value.periodKey === filter.periodKey,
                )}
                onChange={(e: any) => {
                  handleDateRangeChange(
                    e?.value.accountingPeriodId,
                    e?.value.periodKey,
                    e?.value.fixedFromDate,
                    e?.value.fixedToDate,
                    e?.value.varFromDate,
                    e?.value.varToDate,
                  );
                }}
              />
            </div>
            <div className="w-1/3 sm:w-1/5">
              <button
                type="submit"
                className="btn-secondary"
                disabled={
                  !filter.report || !filter.fixedFromDate || !filter.fixedToDate
                }
              >
                {t("reporting.get_data")}
              </button>
            </div>
          </form>
          {SelectedReport && (
            <div className="p-4 flex flex-col grow">
              <Suspense fallback={<Loading />}>
                <SelectedReport {...reportProps} />
              </Suspense>
            </div>
          )}
        </div>
      </Card>
    </Layout>
  );
};

export default BillingReports;
